import React, { useState } from 'react';
import './App.css';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import CompleteQubitVisualization from './QubitVisual.jsx';
import QubitEntanglement from './QubitEntanglement.jsx';
import CryptographyPage from './CryptographyPage.jsx';

function App() {
  const [activeView, setActiveView] = useState('home');
  const [activeTab, setActiveTab] = useState('single-qubit');

  const renderVisualizations = () => (
    <div className="min-h-screen bg-gray-50 pt-8 px-4">
      <div className="max-w-screen-2xl mx-auto">
        {/* Tab Content */}
        <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-sm min-h-screen">
          {activeTab === 'single-qubit' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-3">
                  Single Qubit Visualization
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  Observe how a quantum bit exists in superposition and collapses upon measurement. Adjust the angles on the Bloch sphere!
                </p>
              </div>
              <CompleteQubitVisualization />
            </div>
          )}

          {activeTab === 'entanglement' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-3">
                  Quantum Entanglement (3-Qubit System)
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  Measure the qubits to see how entangled particles influence each other instantaneously. The first two qubits are entangled!
                </p>
              </div>
              <QubitEntanglement />
            </div>
          )}

          {activeTab === 'learn' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-3">
                  📚 Quantum Computing for New Learners
                </h2>
                <p className="text-gray-600 text-lg">
                  Essential concepts to understand before exploring the visualizations
                </p>
              </div>

              {/* Learning Cards */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Qubit Card */}
                <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all">
                  <h3 className="text-2xl font-bold text-blue-700 mb-3 flex items-center">
                    <span className="text-3xl mr-2">⚛️</span> What is a Qubit?
                  </h3>
                  <p className="text-gray-600 mb-4">
                    A quantum bit (qubit) is the quantum version of a classical bit. Unlike classical bits that are either 0 or 1, qubits can exist in a superposition of both states simultaneously.
                  </p>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>✓ Can be |0⟩ (read as "ket zero")</li>
                    <li>✓ Can be |1⟩ (read as "ket one")</li>
                    <li>✓ Can be both |0⟩ and |1⟩ at the same time (superposition)</li>
                  </ul>
                </div>

                {/* Bloch Sphere Card */}
                <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-purple-300 transition-all">
                  <h3 className="text-2xl font-bold text-purple-700 mb-3 flex items-center">
                    <span className="text-3xl mr-2">🌐</span> Bloch Sphere
                  </h3>
                  <p className="text-gray-600 mb-4">
                    The Bloch sphere is a geometric representation of a single qubit's state. The surface of the sphere represents all possible quantum states.
                  </p>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>✓ North pole (↑) represents |0⟩</li>
                    <li>✓ South pole (↓) represents |1⟩</li>
                    <li>✓ Equator represents superposition states</li>
                  </ul>
                </div>

                {/* Superposition Card */}
                <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-teal-300 transition-all">
                  <h3 className="text-2xl font-bold text-teal-700 mb-3 flex items-center">
                    <span className="text-3xl mr-2">🌊</span> Superposition
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Superposition is when a qubit exists in multiple states at once. When measured, it "collapses" into either 0 or 1.
                  </p>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>✓ |ψ⟩ = α|0⟩ + β|1⟩ (mathematical notation)</li>
                    <li>✓ α and β are probability amplitudes</li>
                    <li>✓ Measurement gives 0 or 1 based on probabilities</li>
                  </ul>
                </div>

                {/* Measurement Card */}
                <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-orange-300 transition-all">
                  <h3 className="text-2xl font-bold text-orange-700 mb-3 flex items-center">
                    <span className="text-3xl mr-2">🔍</span> Measurement
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Measuring a qubit forces it to "choose" between 0 or 1. The probability depends on its quantum state before measurement.
                  </p>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>✓ Before measurement: superposition exists</li>
                    <li>✓ After measurement: definite 0 or 1</li>
                    <li>✓ Irreversible process (quantum state collapses)</li>
                  </ul>
                </div>

                {/* Entanglement Card */}
                <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-pink-300 transition-all">
                  <h3 className="text-2xl font-bold text-pink-700 mb-3 flex items-center">
                    <span className="text-3xl mr-2">🔗</span> Entanglement
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Entanglement occurs when two or more qubits become correlated. Measuring one instantly affects the others, even if far apart!
                  </p>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>✓ Qubits share a quantum state</li>
                    <li>✓ Measurement outcomes are correlated</li>
                    <li>✓ Einstein called this "spooky action at a distance"</li>
                  </ul>
                </div>

                {/* Tips Card */}
                <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-amber-300 transition-all">
                  <h3 className="text-2xl font-bold text-amber-700 mb-3 flex items-center">
                    <span className="text-3xl mr-2">💡</span> Tips for Learning
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Make the most of the interactive visualizations:
                  </p>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>✓ Start with Single Qubit tab to understand basics</li>
                    <li>✓ Adjust sliders and observe the Bloch sphere</li>
                    <li>✓ Then explore Entanglement for multi-qubit concepts</li>
                  </ul>
                </div>
              </div>

              {/* Quick Navigation */}
              <div className="text-center mt-12 space-y-4">
                <p className="text-gray-700 text-lg font-semibold">Ready to explore?</p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <button
                    onClick={() => setActiveTab('single-qubit')}
                    className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all transform hover:scale-105 shadow-md"
                  >
                    Start with Single Qubit
                  </button>
                  <button
                    onClick={() => setActiveTab('entanglement')}
                    className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all transform hover:scale-105 shadow-md"
                  >
                    Explore Entanglement
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch(activeView) {
      case 'visualizations':
        return renderVisualizations();
      case 'cryptography':
        return (
          <div className="min-h-screen bg-gray-50">
            <div className="max-w-screen-2xl mx-auto">
              <CryptographyPage />
            </div>
          </div>
        );
      default:
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="w-full max-w-7xl mx-auto p-4 sm:p-8 text-center">
              {/* Header Section */}
              <div className="mb-12">
                <div className="flex items-center justify-center mb-4 px-2">
                  <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 text-center">
                    {/* Q with embedded qubit */}
                    <span className="relative inline-block mr-1">
                      Q
                      {/* Larger animated qubit inside Q for better visibility */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/4 scale-[0.55] sm:scale-75 md:scale-90 lg:scale-100">
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 100 100"
                          className="animate-spin-slow"
                          style={{animationDuration: '5s'}}
                        >
                          {/* Bloch Sphere - more visible */}
                          <circle
                            cx="50"
                            cy="50"
                            r="30"
                            fill="none"
                            stroke="url(#gradient1)"
                            strokeWidth="5"
                            opacity="1"
                          />
                          {/* Equator - more prominent */}
                          <ellipse
                            cx="50"
                            cy="50"
                            rx="30"
                            ry="7"
                            fill="none"
                            stroke="url(#gradient2)"
                            strokeWidth="3"
                            opacity="0.8"
                          />
                          {/* State Vector - more visible */}
                          <line
                            x1="50"
                            y1="50"
                            x2="68"
                            y2="32"
                            stroke="url(#gradient3)"
                            strokeWidth="6"
                            className="animate-pulse"
                          />
                          {/* Arrow head - larger */}
                          <polygon
                            points="68,32 60,26 60,38"
                            fill="url(#gradient3)"
                            className="animate-pulse"
                          />

                          {/* Enhanced Gradients */}
                          <defs>
                            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#0078D4" />
                              <stop offset="50%" stopColor="#5C2D91" />
                              <stop offset="100%" stopColor="#00B7C3" />
                            </linearGradient>
                            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#5C2D91" />
                              <stop offset="100%" stopColor="#0078D4" />
                            </linearGradient>
                            <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#F7A800" />
                              <stop offset="50%" stopColor="#D97706" />
                              <stop offset="100%" stopColor="#F7A800" />
                            </linearGradient>
                          </defs>
                        </svg>

                        {/* More visible floating particles */}
                        <div className="absolute -top-1 -left-1 w-1.5 h-1.5 bg-cyan-500 rounded-full animate-ping opacity-90"></div>
                        <div className="absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-purple-500 rounded-full animate-ping opacity-90" style={{animationDelay: '1s'}}></div>
                        <div className="absolute top-0 -right-1 w-1 h-1 bg-blue-500 rounded-full animate-ping opacity-75" style={{animationDelay: '2s'}}></div>
                      </div>
                    </span>
                    uantum Visualizations
                  </h1>
                </div>

                <p className="text-xl sm:text-2xl md:text-3xl text-gray-600 mb-6 font-light">
                  Interactive quantum computing demonstrations
                </p>

                {/* Event highlight */}
                <div className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 bg-blue-50 rounded-full px-4 sm:px-6 py-2.5 sm:py-3 border border-blue-200 mb-8">
                  <span className="text-lg sm:text-xl">🎉</span>
                  <span className="text-gray-700 text-sm sm:text-base font-semibold">
                    Live at the <span className="text-purple-700 font-bold">AICTE IDEA Lab</span> Grand Inauguration
                  </span>
                  <span className="hidden sm:inline text-gray-500 text-sm">&bull; Sept 11, 2026</span>
                </div>
              </div>

              {/* Visualization Cards */}
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                {/* Single Qubit Card */}
                <div
                  onClick={() => setActiveView('visualizations')}
                  className="group cursor-pointer bg-white rounded-3xl p-8 border border-gray-200 shadow-sm hover:border-blue-300 transform hover:scale-105 hover:-translate-y-2 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="text-8xl mb-6 group-hover:animate-bounce">🌊</div>
                  <h2 className="text-4xl font-bold text-blue-700 mb-4 group-hover:text-blue-600 transition-colors">
                    Single Qubit
                  </h2>
                  <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                    Explore quantum superposition, measurement collapse, and the elegant Bloch sphere representation of a single quantum bit.
                  </p>

                  {/* Feature Tags */}
                  <div className="flex justify-center flex-wrap gap-3 mb-6">
                    <span className="bg-blue-50 px-4 py-2 rounded-full text-sm font-semibold text-blue-700 border border-blue-200">
                      🎯 Superposition
                    </span>
                    <span className="bg-cyan-50 px-4 py-2 rounded-full text-sm font-semibold text-cyan-700 border border-cyan-200">
                      🌐 Bloch Sphere
                    </span>
                    <span className="bg-teal-50 px-4 py-2 rounded-full text-sm font-semibold text-teal-700 border border-teal-200">
                      🔍 Measurement
                    </span>
                  </div>

                  <div className="mt-8 text-blue-600 group-hover:text-blue-700 transition-colors text-lg font-semibold">
                    Click to explore quantum mechanics →
                  </div>
                </div>

                {/* Three Qubit Card */}
                <div
                  onClick={() => setActiveView('visualizations')}
                  className="group cursor-pointer bg-white rounded-3xl p-8 border border-gray-200 shadow-sm hover:border-purple-300 transform hover:scale-105 hover:-translate-y-2 transition-all duration-300 hover:shadow-xl"
                >
                  <div className="text-8xl mb-6 group-hover:animate-bounce">🔗</div>
                  <h2 className="text-4xl font-bold text-purple-700 mb-4 group-hover:text-purple-600 transition-colors">
                    3-Qubit System
                  </h2>
                  <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                    Discover the mysterious world of quantum entanglement with 2 entangled qubits and 1 independent qubit system.
                  </p>

                  {/* Feature Tags */}
                  <div className="flex justify-center flex-wrap gap-3 mb-6">
                    <span className="bg-purple-50 px-4 py-2 rounded-full text-sm font-semibold text-purple-700 border border-purple-200">
                      🔗 Entanglement
                    </span>
                    <span className="bg-indigo-50 px-4 py-2 rounded-full text-sm font-semibold text-indigo-700 border border-indigo-200">
                      ⚡ Correlation
                    </span>
                    <span className="bg-violet-50 px-4 py-2 rounded-full text-sm font-semibold text-violet-700 border border-violet-200">
                      🎭 Multi-Qubit
                    </span>
                  </div>

                  <div className="mt-8 text-purple-600 group-hover:text-purple-700 transition-colors text-lg font-semibold">
                    Click to explore entanglement →
                  </div>
                </div>
              </div>

              {/* Cryptography Card */}
                <div
                  onClick={() => setActiveView('cryptography')}
                  className="group cursor-pointer bg-white rounded-3xl p-8 border border-gray-200 shadow-sm hover:border-teal-300 transform hover:scale-105 hover:-translate-y-2 transition-all duration-300 hover:shadow-xl md:col-span-2"
                >
                  <div className="text-8xl mb-6 group-hover:animate-bounce">🔐</div>
                  <h2 className="text-4xl font-bold text-teal-700 mb-4 group-hover:text-teal-600 transition-colors">
                    Cryptography
                  </h2>
                  <p className="text-gray-600 mb-6 text-lg leading-relaxed max-w-2xl mx-auto">
                    Compare classical encryption (AES, RSA) with Quantum Key Distribution (BB84). See why quantum cryptography makes eavesdropping impossible to hide — with live simulated attacks.
                  </p>
                  <div className="flex justify-center flex-wrap gap-3 mb-6">
                    <span className="bg-teal-50 px-4 py-2 rounded-full text-sm font-semibold text-teal-700 border border-teal-200">🔑 Classical vs Quantum</span>
                    <span className="bg-purple-50 px-4 py-2 rounded-full text-sm font-semibold text-purple-700 border border-purple-200">⚛️ QKD BB84</span>
                    <span className="bg-pink-50 px-4 py-2 rounded-full text-sm font-semibold text-pink-700 border border-pink-200">🕵️ Live Attack Sim</span>
                  </div>
                  <div className="mt-4 text-teal-600 group-hover:text-teal-700 transition-colors text-lg font-semibold">
                    Click to explore quantum cryptography →
                  </div>
                </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="App flex flex-col min-h-screen">
      <Header
        activeView={activeView}
        activeTab={activeTab}
        onNavigate={(view, tab) => {
          setActiveView(view);
          if (tab) setActiveTab(tab);
        }}
      />
      <main className="flex-1">
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
}

export default App;
